import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import WorkZonesPicker from "@/components/WorkZonesPicker";
import BoostInfoBanner from "@/components/BoostInfoBanner";
import { useAuth } from "@/context/AuthContext";
import { resolvePhotoUrl } from "@/lib/photos";

const EMPTY = {
  title: "", description: "", category_slug: "", city: "", region: "", cap: "",
  contact_email: "", contact_phone: "", whatsapp: "", website: "", video_url: "",
  photos: [],
  zone_online: false, zone_regioni: [], zone_comuni: [],
};

export default function ListingForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const justFeatured = params.get("just_featured") === "1";
  const nav = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [photoInput, setPhotoInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const sub = user?.subscription;
  const hasActiveSub = !!(sub && new Date(sub.expires_at) > new Date());

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    if (id) {
      api.get(`/listings/${id}`).then((r) => {
        const l = r.data;
        setForm({
          title: l.title || "", subtitle: l.subtitle || "", description: l.description || "", category_slug: l.category_slug,
          city: l.city || "", region: l.region || "", cap: l.cap || "",
          contact_email: l.contact_email || "", contact_phone: l.contact_phone || "",
          whatsapp: l.whatsapp || "", website: l.website || "", video_url: l.video_url || "",
          photos: l.photos || [],
          zone_online: !!l.zone_online, zone_regioni: l.zone_regioni || [], zone_comuni: l.zone_comuni || [],
        });
      });
    }
  }, [id]);

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addPhoto = () => {
    if (!photoInput.trim()) return;
    up("photos", [...form.photos, photoInput.trim()]);
    setPhotoInput("");
  };
  const removePhoto = (i) => up("photos", form.photos.filter((_, idx) => idx !== i));

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    const urls = [];
    for (const f of Array.from(files)) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name}: non è un'immagine`); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: troppo grande (>10MB)`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      } catch (e) {
        toast.error(`${f.name}: upload fallito`);
      }
    }
    if (urls.length) up("photos", [...form.photos, ...urls]);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  // Used by the FeaturedSlotPromo CTA when the listing hasn't been saved yet.
  // Returns the listing id on success (so the caller can navigate to /checkout).
  const saveFirstForFeatured = async () => {
    if (!form.category_slug) { toast.error("Seleziona prima una categoria"); return null; }
    if (!form.title.trim()) { toast.error("Inserisci un titolo"); return null; }
    const payload = { ...form, contact_email: form.contact_email || null };
    if (id) {
      await api.put(`/listings/${id}`, payload);
      return id;
    }
    const { data } = await api.post("/listings", payload);
    toast.success("Annuncio salvato. Procedi al pagamento.");
    return data.id;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category_slug) { toast.error("Seleziona una categoria"); return; }
    setLoading(true);
    try {
      const payload = { ...form, contact_email: form.contact_email || null };
      if (id) {
        await api.put(`/listings/${id}`, payload);
        toast.success("Annuncio aggiornato");
        nav("/dashboard");
        return;
      }
      const { data } = await api.post("/listings", payload);
      if (!hasActiveSub) {
        // No active subscription → send user straight to pricing/checkout
        toast.success("Annuncio creato! Scegli un piano per pubblicarlo online.");
        nav(`/prezzi?listing_id=${encodeURIComponent(data.id)}&new=1`);
        return;
      }
      toast.success("Annuncio creato e pubblicato online.");
      nav("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-12" data-testid="listing-form-page">
      <Link to="/dashboard" className="text-xs text-zinc-500 hover:text-[#FFBE00]">← Dashboard</Link>
      <h1 className="font-heading font-black text-4xl sm:text-5xl mt-4 mb-10">{id ? "Modifica annuncio" : "Nuovo annuncio"}</h1>

      {justFeatured && (
        <div className="mb-8 p-5 rounded-xl border-2 border-[#22C55E] bg-gradient-to-r from-[#22C55E]/10 to-[#FFBE00]/10 flex items-start gap-4 flex-wrap" data-testid="listing-form-featured-banner">
          <i className="fas fa-bolt text-[#FFBE00] text-3xl hm-promo-spin"></i>
          <div className="flex-1 min-w-[200px]">
            <div className="font-heading font-black text-xl">Il tuo annuncio è ora in home page 🎉</div>
            <div className="text-sm text-zinc-300 mt-1">Completa eventuali dettagli e conferma con il tasto <strong>"Crea annuncio"</strong> qui sotto.</div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-8" data-testid="listing-form">
        <section className="hm-card p-6 space-y-4">
          <div className="text-xs uppercase tracking-widest text-[#FFBE00]">· Info principali</div>
          <Field label="Titolo *">
            <input required value={form.title} onChange={(e) => up("title", e.target.value)} className={inputCls} data-testid="form-title" />
          </Field>
          <Field label="Sottotitolo (tagline, 1 riga)">
            <input value={form.subtitle} onChange={(e) => up("subtitle", e.target.value)} placeholder="Es. Fotografo specializzato in affitti brevi a Roma" className={inputCls} data-testid="form-subtitle" />
          </Field>
          <Field label="Categoria *">
            <select required value={form.category_slug} onChange={(e) => up("category_slug", e.target.value)} className={inputCls} data-testid="form-category">
              <option value="">Seleziona…</option>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name} · {c.tier}</option>)}
            </select>
          </Field>
          <Field label="Descrizione">
            <textarea rows={8} value={form.description} onChange={(e) => up("description", e.target.value)} className={inputCls} data-testid="form-description" />
          </Field>
        </section>

        <section className="hm-card p-6 space-y-4">
          <div className="text-xs uppercase tracking-widest text-[#E619A8]">· Dove ti trovi</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Città"><input value={form.city} onChange={(e) => up("city", e.target.value)} className={inputCls} data-testid="form-city" /></Field>
            <Field label="Regione"><input value={form.region} onChange={(e) => up("region", e.target.value)} className={inputCls} data-testid="form-region" /></Field>
            <Field label="CAP"><input value={form.cap} onChange={(e) => up("cap", e.target.value.replace(/[^0-9]/g, "").slice(0, 5))} placeholder="es. 16121" maxLength={5} inputMode="numeric" pattern="[0-9]{5}" className={inputCls} data-testid="form-cap" /></Field>
          </div>
        </section>

        <section className="hm-card p-6 space-y-4">
          <div className="text-xs uppercase tracking-widest text-[#FFBE00]">· Zone in cui lavori</div>
          <p className="text-xs text-zinc-500 -mt-2">Seleziona una o più opzioni: online in tutta Italia, regioni, comuni specifici. Aiuta i clienti a trovarti.</p>
          <WorkZonesPicker
            value={{ zone_online: form.zone_online, zone_regioni: form.zone_regioni, zone_comuni: form.zone_comuni }}
            onChange={(v) => setForm((f) => ({ ...f, ...v }))}
          />
        </section>

        <section className="hm-card p-6 space-y-4">
          <div className="text-xs uppercase tracking-widest text-[#FFBE00]">· Contatti</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Email"><input type="email" value={form.contact_email} onChange={(e) => up("contact_email", e.target.value)} className={inputCls} data-testid="form-email" /></Field>
            <Field label="Telefono"><input value={form.contact_phone} onChange={(e) => up("contact_phone", e.target.value)} className={inputCls} data-testid="form-phone" /></Field>
            <Field label="WhatsApp (numero con prefisso)"><input placeholder="+39..." value={form.whatsapp} onChange={(e) => up("whatsapp", e.target.value)} className={inputCls} data-testid="form-whatsapp" /></Field>
            <Field label="Sito web"><input placeholder="https://..." value={form.website} onChange={(e) => up("website", e.target.value)} className={inputCls} data-testid="form-website" /></Field>
          </div>
        </section>

        <section className="hm-card p-6 space-y-4">
          <div className="text-xs uppercase tracking-widest text-[#E619A8]">· Media</div>
          <Field label="URL Video (YouTube, Vimeo, Loom…)">
            <input value={form.video_url} onChange={(e) => up("video_url", e.target.value)} className={inputCls} data-testid="form-video" />
          </Field>
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Foto</div>
            <div className="flex flex-col sm:flex-row gap-2 mb-2">
              <input ref={fileRef} type="file" multiple accept="image/*" onChange={(e) => uploadFiles(e.target.files)} className="block text-sm text-zinc-300 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-bold file:uppercase file:bg-[#E619A8] file:text-white hover:file:bg-[#FFBE00] hover:file:text-black" data-testid="form-upload-file" />
              {uploading && <span className="text-xs text-[#FFBE00] self-center"><i className="fas fa-circle-notch fa-spin mr-2"></i>Upload in corso…</span>}
            </div>
            <div className="flex gap-2">
              <input value={photoInput} onChange={(e) => setPhotoInput(e.target.value)} placeholder="Oppure incolla URL https://..." className={inputCls} data-testid="form-photo-input" />
              <button type="button" onClick={addPhoto} className="px-4 py-2 bg-[#FFBE00] text-black text-xs font-bold uppercase rounded-md" data-testid="form-add-photo">Aggiungi</button>
            </div>
            {form.photos.length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-3">
                {form.photos.map((p, i) => (
                  <div key={i} className="relative group">
                    <img src={resolvePhotoUrl(p)} alt="" className="w-full aspect-square object-cover rounded-md border border-zinc-800" />
                    <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/80 w-6 h-6 rounded text-xs opacity-0 group-hover:opacity-100 transition">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* HOME SPOTLIGHT — informational only (no CTA). Purchase is done later from dashboard. */}
        <BoostInfoBanner />

        <div className="flex gap-3 justify-end">
          <button type="button" onClick={() => nav("/dashboard")} className="px-5 py-3 border border-zinc-700 rounded-md text-sm uppercase tracking-wider">Annulla</button>
          <button type="submit" disabled={loading} className="px-6 py-3 bg-[#E619A8] text-white font-bold uppercase tracking-wider text-sm rounded-md hover:bg-[#FFBE00] hover:text-black transition-all disabled:opacity-50" data-testid="form-submit">
            {loading ? "Salvataggio…" : (id ? "Salva modifiche" : (hasActiveSub ? "Crea annuncio" : "Crea e vai al checkout"))}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full bg-[#0A0A0A] border border-zinc-800 rounded-md px-4 py-2.5 text-sm focus:border-[#FFBE00] focus:ring-1 focus:ring-[#FFBE00] outline-none";
const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">{label}</label>
    {children}
  </div>
);
